import React, { useState } from 'react';
import {
  Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import { postService, Post, PostVisibility } from '../services/postService';
import { useDiveSiteCatalog } from '../hooks/useDiveSiteCatalog';
import { useNotification } from './NotificationContext';

interface CheckInDialogProps {
  open: boolean;
  onClose: () => void;
  onCheckedIn: (post: Post) => void;
  /** Pre-select a site (the check-in button on a dive site page). */
  site?: { slug: string; name: string };
}

/** "I'm diving at X today": pick a catalog site, add an optional note, choose who sees it. */
const CheckInDialog: React.FC<CheckInDialogProps> = ({ open, onClose, onCheckedIn, site }) => {
  const { showSuccess, showError } = useNotification();
  const { allSites } = useDiveSiteCatalog();
  const [selected, setSelected] = useState<{ slug: string; name: string } | null>(site ?? null);
  const [note, setNote] = useState('');
  const [visibility, setVisibility] = useState<string>(PostVisibility.Buddies);
  const [submitting, setSubmitting] = useState(false);

  const options = allSites.map(s => ({ slug: s.slug, name: s.name, label: s.region ? `${s.name} — ${s.region}` : s.name }));

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const post = await postService.createCheckIn({
        diveSiteSlug: selected.slug,
        diveSiteName: selected.name,
        note: note.trim() || undefined,
        visibility,
      });
      showSuccess(`Checked in at ${selected.name}`);
      onCheckedIn(post);
      onClose();
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to check in');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Check in</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {site ? (
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Diving at {site.name}</Typography>
          ) : (
            <Autocomplete
              options={options}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.slug === b.slug}
              onChange={(_, value) => setSelected(value ? { slug: value.slug, name: value.name } : null)}
              renderInput={(params) => <TextField {...params} label="Where are you diving?" placeholder="Search dive sites…" autoFocus />}
            />
          )}
          <TextField
            label="Add a note (optional)"
            placeholder="Conditions, who you're with, what you hope to see…"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 280))}
            multiline
            minRows={2}
            helperText={`${note.length}/280`}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Who can see this</InputLabel>
            <Select value={visibility} label="Who can see this" onChange={(e) => setVisibility(e.target.value)}>
              <MenuItem value={PostVisibility.Public}>Public — also shows on the site's community page</MenuItem>
              <MenuItem value={PostVisibility.Buddies}>Dive Buddies only</MenuItem>
              <MenuItem value={PostVisibility.Followers}>Followers only</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!selected || submitting}>
          {submitting ? <CircularProgress size={20} /> : 'Check in'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckInDialog;
